/*
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 * Original code available from https://github.com/okta/okta-react/blob/master/src/LoginCallback.tsx
 */

import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import { useOktaAuth } from '@okta/okta-react';
import { OnAuthResumeFunction } from '@okta/okta-react/bundles/types/OktaContext';

import LogoWhite from '../../../assets/images/LogoWhite';
import OktaError from './OktaError';

interface LoginCallbackProps {
  errorComponent?: React.ComponentType<{ error: Error }>;
  onAuthResume?: OnAuthResumeFunction;
}

const useStyles = makeStyles((theme: Theme) => ({
  logoWrapper: {
    width: '100%',
  },
  logo: {
    [theme.breakpoints.down('xs')]: {
      transform: 'scale(0.75)',
      transformOrigin: 'left top',
    },
  },
}));

const LoginCallback: React.FC<LoginCallbackProps> = ({ errorComponent, onAuthResume }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const classes = useStyles();
  const [callbackError, setCallbackError] = React.useState(null);

  const ErrorReporter = errorComponent || OktaError;
  React.useEffect(() => {
    const isInteractionRequired =
      // @ts-ignore OKTA-464505: backward compatibility support for auth-js@5
      oktaAuth.idx.isInteractionRequired || oktaAuth.isInteractionRequired.bind(oktaAuth);
    if (onAuthResume && isInteractionRequired()) {
      onAuthResume();
      return;
    }

    // original code redirects to / by default. Don't want to see log in screen again then redirect to /home
    // passing undefined due to token being saved and accessed from local storage
    oktaAuth
      .handleLoginRedirect(undefined, `${window.location.origin}/login/callback/wrapper`)
      .catch((e) => {
        setCallbackError(e);
      });
  }, [oktaAuth]);

  const authError = authState?.error;
  const displayError = callbackError || authError;
  if (displayError) {
    return <ErrorReporter error={displayError} />;
  }

  return (
    <div className={classes.logoWrapper}>
      <LogoWhite className={classes.logo} />
    </div>
  );
};

export default LoginCallback;
